import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

class ProductAccessorySelector extends HTMLElement {
  constructor() {
    super();
    this.selectedItems = new Map();
    this.processing = false;
    this.abortController = new AbortController();
  }

  connectedCallback() {
    if (this.dataset.accessorySelectorInitialized === 'true') return;
    if (this.abortController.signal.aborted) {
      this.abortController = new AbortController();
    }

    this.dataset.accessorySelectorInitialized = 'true';
    this.productForm = this.closest('product-form-component');
    this.selectedContainer = this.querySelector('[data-accessory-selected]');
    this.selectedList = this.querySelector('[data-accessory-selected-list]');
    this.errorElement = this.querySelector('[data-accessory-error]');

    const { signal } = this.abortController;

    this.querySelectorAll('[data-accessory-row]').forEach((row) => {
      this.bindRow(row, signal);
      this.syncRow(row);
    });

    this.productForm?.addEventListener(ThemeEvents.cartUpdate, this.handleParentCartUpdate, { signal });
    document.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate, { signal });
    document.addEventListener('click', this.handleDocumentClick, { signal });
    this.renderSelectedItems();
  }

  disconnectedCallback() {
    this.abortController.abort();
    delete this.dataset.accessorySelectorInitialized;
  }

  /**
   * @param {Element} row
   * @param {AbortSignal} signal
   */
  bindRow(row, signal) {
    const picker = row.querySelector('[data-accessory-variant-picker]');
    const trigger = row.querySelector('[data-accessory-variant-trigger]');
    const addButton = row.querySelector('[data-accessory-add]');
    const minusButton = row.querySelector('[data-accessory-quantity-minus]');
    const plusButton = row.querySelector('[data-accessory-quantity-plus]');
    const quantityInput = row.querySelector('[data-accessory-quantity-input]');
    const options = row.querySelectorAll('[data-accessory-variant-option]');

    picker?.addEventListener('click', (event) => event.stopPropagation(), { signal });
    trigger?.addEventListener('click', () => this.toggleDropdown(picker), { signal });
    trigger?.addEventListener('keydown', (event) => this.handleTriggerKeydown(event, picker), { signal });
    options.forEach((option) => {
      option.addEventListener('click', (event) => this.handleOptionClick(row, option, event), { signal });
      option.addEventListener('mouseenter', () => this.showTooltip(option), { signal });
      option.addEventListener('mouseleave', () => this.hideTooltip(), { signal });
      option.addEventListener('focus', () => this.showTooltip(option), { signal });
      option.addEventListener('blur', () => this.hideTooltip(), { signal });
    });
    addButton?.addEventListener('click', () => this.addSelection(row), { signal });
    minusButton?.addEventListener('click', () => this.adjustQuantity(row, -1), { signal });
    plusButton?.addEventListener('click', () => this.adjustQuantity(row, 1), { signal });
    quantityInput?.addEventListener('change', () => this.normalizeQuantity(row), { signal });
    quantityInput?.addEventListener('input', () => this.syncQuantityButtons(row), { signal });
  }

  /** @param {Element} row */
  syncRow(row) {
    const selectedVariant = this.getSelectedVariant(row);
    const priceElement = row.querySelector('[data-accessory-price]');
    const addButton = row.querySelector('[data-accessory-add]');
    const triggerText = row.querySelector('[data-accessory-variant-trigger-text]');

    if (priceElement && selectedVariant?.priceText) {
      priceElement.textContent = selectedVariant.priceText;
    }

    if (triggerText && selectedVariant?.title) {
      triggerText.textContent = selectedVariant.title;
    }

    if (addButton instanceof HTMLButtonElement) {
      addButton.disabled = !selectedVariant?.available || this.processing;
    }

    this.syncQuantityButtons(row);
  }

  /** @param {Element} row */
  syncQuantityButtons(row) {
    const quantity = this.getRowQuantity(row);
    const minusButton = row.querySelector('[data-accessory-quantity-minus]');

    if (minusButton instanceof HTMLButtonElement) {
      minusButton.disabled = quantity <= 1 || this.processing;
    }
  }

  /**
   * @param {Element} row
   * @param {number} direction
   */
  adjustQuantity(row, direction) {
    const quantityInput = row.querySelector('[data-accessory-quantity-input]');
    if (!(quantityInput instanceof HTMLInputElement)) return;

    const nextValue = Math.max(1, this.getRowQuantity(row) + direction);
    quantityInput.value = nextValue.toString();
    this.syncQuantityButtons(row);
  }

  /** @param {Element} row */
  normalizeQuantity(row) {
    const quantityInput = row.querySelector('[data-accessory-quantity-input]');
    if (!(quantityInput instanceof HTMLInputElement)) return;

    quantityInput.value = this.getRowQuantity(row).toString();
    this.syncQuantityButtons(row);
  }

  /** @param {Element} row */
  getRowQuantity(row) {
    const quantityInput = row.querySelector('[data-accessory-quantity-input]');
    if (!(quantityInput instanceof HTMLInputElement)) return 1;

    const quantity = Number.parseInt(quantityInput.value, 10);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  /** @param {Element} row */
  getSelectedVariant(row) {
    const hiddenInput = row.querySelector('[data-accessory-variant-input]');

    if (hiddenInput instanceof HTMLInputElement) {
      return {
        available: hiddenInput.dataset.available !== 'false',
        image: hiddenInput.dataset.image || row.getAttribute('data-product-image') || '',
        price: Number.parseInt(hiddenInput.dataset.price || '0', 10) || 0,
        priceText: hiddenInput.dataset.priceText || '',
        title: hiddenInput.dataset.title || '',
        variantId: hiddenInput.value,
      };
    }

    return null;
  }

  /**
   * @param {Element | null} picker
   * @param {boolean} [forceOpen]
   */
  toggleDropdown(picker, forceOpen) {
    if (!(picker instanceof HTMLElement) || this.processing) return;

    const shouldOpen = forceOpen ?? !picker.classList.contains('is-open');
    this.closeAllDropdowns(picker);

    const trigger = picker.querySelector('[data-accessory-variant-trigger]');
    const menu = picker.querySelector('[data-accessory-variant-menu]');

    picker.classList.toggle('is-open', shouldOpen);
    menu?.classList.toggle('is-open', shouldOpen);
    trigger?.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');

    if (!shouldOpen) {
      this.hideTooltip();
    }
  }

  /** @param {Element | null} except */
  closeAllDropdowns(except = null) {
    this.querySelectorAll('[data-accessory-variant-picker].is-open').forEach((picker) => {
      if (picker === except) return;

      picker.classList.remove('is-open');
      picker.querySelector('[data-accessory-variant-menu]')?.classList.remove('is-open');
      picker.querySelector('[data-accessory-variant-trigger]')?.setAttribute('aria-expanded', 'false');
    });
    this.hideTooltip();
  }

  /** @param {Event} event */
  handleDocumentClick = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.closeAllDropdowns();
  };

  /**
   * @param {KeyboardEvent} event
   * @param {Element | null} picker
   */
  handleTriggerKeydown(event, picker) {
    if (event.key !== 'ArrowDown' && event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    this.toggleDropdown(picker, true);
    picker?.querySelector('[data-accessory-variant-option].is-selected')?.focus();
  }

  /**
   * @param {Element} row
   * @param {Element} option
   * @param {MouseEvent} event
   */
  handleOptionClick(row, option, event) {
    if (
      this.isMobileViewport() &&
      option.classList.contains('has-image') &&
      event.target instanceof Element &&
      event.target.closest('.product-accessory-selector__dropdown-swatch')
    ) {
      event.preventDefault();
      event.stopPropagation();
      this.toggleMobilePreview(option);
      return;
    }

    this.selectOption(row, option);
  }

  /**
   * @param {Element} option
   */
  toggleMobilePreview(option) {
    if (this.mobileTooltipOption === option && this.tooltip?.classList.contains('is-visible')) {
      this.hideTooltip();
      return;
    }

    const imageUrl = option.getAttribute('data-image');
    if (!imageUrl) return;

    const tooltip = this.getTooltip();
    const tooltipImage = tooltip.querySelector('img');
    if (!(tooltipImage instanceof HTMLImageElement)) return;

    const tooltipSize = this.getTooltipSize();
    const viewportInset = 16;
    const gap = 10;
    const renderedSize = Math.min(tooltipSize, window.innerWidth - viewportInset * 2);
    const optionRect = option.getBoundingClientRect();
    let left = optionRect.left + optionRect.width / 2 - renderedSize / 2;
    let top = optionRect.bottom + gap;

    if (top + renderedSize > window.innerHeight - viewportInset) {
      top = optionRect.top - renderedSize - gap;
    }

    const maxLeft = Math.max(viewportInset, window.innerWidth - renderedSize - viewportInset);
    const maxTop = Math.max(viewportInset, window.innerHeight - renderedSize - viewportInset);
    left = Math.min(Math.max(left, viewportInset), maxLeft);
    top = Math.min(Math.max(top, viewportInset), maxTop);

    tooltipImage.src = imageUrl;
    tooltipImage.alt = option.textContent?.trim() || '';
    tooltip.style.setProperty('--pas-tooltip-size', `${tooltipSize}px`);
    tooltip.classList.add('is-mobile');
    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${top}px`;
    tooltip.classList.add('is-visible');
    this.mobileTooltipOption = option;
  }

  isMobileViewport() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  /**
   * @param {Element} row
   * @param {Element} option
   */
  selectOption(row, option) {
    if (!(option instanceof HTMLButtonElement) || option.disabled) return;

    const hiddenInput = row.querySelector('[data-accessory-variant-input]');
    if (!(hiddenInput instanceof HTMLInputElement)) return;

    hiddenInput.value = option.dataset.variantId || '';
    hiddenInput.dataset.title = option.dataset.title || '';
    hiddenInput.dataset.price = option.dataset.price || '0';
    hiddenInput.dataset.priceText = option.dataset.priceText || '';
    hiddenInput.dataset.image = option.dataset.image || row.getAttribute('data-product-image') || '';
    hiddenInput.dataset.available = option.disabled ? 'false' : 'true';

    row.querySelectorAll('[data-accessory-variant-option]').forEach((item) => {
      const isSelected = item === option;
      item.classList.toggle('is-selected', isSelected);
      item.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });

    this.syncRow(row);
    this.toggleDropdown(row.querySelector('[data-accessory-variant-picker]'), false);
    row.querySelector('[data-accessory-variant-trigger]')?.focus();
  }

  /** @param {Element} option */
  showTooltip(option) {
    const imageUrl = option.getAttribute('data-image');
    if (!imageUrl || this.isMobileViewport()) return;

    const tooltip = this.getTooltip();
    const tooltipImage = tooltip.querySelector('img');
    if (!(tooltipImage instanceof HTMLImageElement)) return;

    tooltipImage.src = imageUrl;
    tooltipImage.alt = option.textContent?.trim() || '';
    const tooltipSize = this.getTooltipSize();
    tooltip.style.setProperty('--pas-tooltip-size', `${tooltipSize}px`);
    tooltip.classList.remove('is-mobile');

    const optionRect = option.getBoundingClientRect();
    const menuRect = option.closest('[data-accessory-variant-menu]')?.getBoundingClientRect() || optionRect;
    const tooltipWidth = tooltipSize;
    const tooltipHeight = tooltipWidth;
    const gap = 12;
    const viewportInset = 8;
    const horizontalInset = 16;
    let left = menuRect.right + gap;
    let top = optionRect.top + optionRect.height / 2 - tooltipHeight / 2;

    if (left + tooltipWidth > window.innerWidth - horizontalInset) {
      left = menuRect.left - tooltipWidth - gap;
    }

    const maxLeft = Math.max(horizontalInset, window.innerWidth - tooltipWidth - horizontalInset);
    left = Math.min(Math.max(left, horizontalInset), maxLeft);

    const maxTop = Math.max(viewportInset, window.innerHeight - tooltipHeight - viewportInset);
    if (top < viewportInset) {
      top = viewportInset;
    } else if (top > maxTop) {
      top = maxTop;
    }

    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${top}px`;
    tooltip.classList.add('is-visible');
  }

  hideTooltip() {
    this.tooltip?.classList.remove('is-visible', 'is-mobile');
    this.mobileTooltipOption = null;
  }

  getTooltip() {
    if (this.tooltip instanceof HTMLElement) return this.tooltip;

    const existingTooltip = document.getElementById('product-accessory-selector-tooltip');
    if (existingTooltip instanceof HTMLElement) {
      this.tooltip = existingTooltip;
      return this.tooltip;
    }

    const tooltip = document.createElement('div');
    tooltip.id = 'product-accessory-selector-tooltip';
    tooltip.className = 'product-accessory-selector__tooltip';
    tooltip.innerHTML = '<img src="" alt="" loading="lazy">';
    document.body.append(tooltip);
    this.tooltip = tooltip;
    return tooltip;
  }

  getTooltipSize() {
    const size = Number.parseInt(this.dataset.tooltipSize || '', 10);
    return Number.isFinite(size) && size > 0 ? size : 140;
  }

  /** @param {Element} row */
  addSelection(row) {
    const selectedVariant = this.getSelectedVariant(row);
    if (!selectedVariant?.available || !selectedVariant.variantId) return;

    const quantity = this.getRowQuantity(row);
    const productId = row.getAttribute('data-product-id') || '';
    const productTitle = row.getAttribute('data-product-title') || '';
    const key = selectedVariant.variantId.toString();
    const existingItem = this.selectedItems.get(key);

    if (existingItem) {
      existingItem.quantity += quantity;
    } else {
      this.selectedItems.set(key, {
        image: selectedVariant.image,
        price: selectedVariant.price,
        priceText: selectedVariant.priceText,
        productId,
        productTitle,
        quantity,
        variantId: selectedVariant.variantId,
        variantTitle: selectedVariant.title,
      });
    }

    this.hideError();
    this.renderSelectedItems();
  }

  renderSelectedItems() {
    if (!this.selectedContainer || !this.selectedList) return;

    this.selectedList.textContent = '';
    const items = Array.from(this.selectedItems.values());

    items.forEach((item) => {
      const listItem = document.createElement('li');
      listItem.className = 'product-accessory-selector__selected-item';

      const title = document.createElement('span');
      title.className = 'product-accessory-selector__selected-title';
      title.textContent = this.getItemLabel(item);

      const removeButton = document.createElement('button');
      removeButton.type = 'button';
      removeButton.className = 'product-accessory-selector__selected-remove';
      removeButton.setAttribute('aria-label', `Remove ${this.getItemLabel(item)}`);
      removeButton.textContent = 'x';
      removeButton.addEventListener('click', () => {
        this.selectedItems.delete(item.variantId.toString());
        this.renderSelectedItems();
      });

      listItem.append(title, removeButton);
      this.selectedList.append(listItem);
    });

    this.selectedContainer.hidden = items.length === 0;
    this.updateAcceleratedCheckout();
  }

  /** @param {{productTitle: string, quantity: number, variantTitle: string}} item */
  getItemLabel(item) {
    const variantTitle = item.variantTitle && item.variantTitle !== 'Default Title' ? ` - ${item.variantTitle}` : '';
    return `${item.quantity} x ${item.productTitle}${variantTitle}`;
  }

  updateAcceleratedCheckout() {
    const acceleratedCheckout = this.productForm?.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
    if (!(acceleratedCheckout instanceof HTMLElement)) return;

    if (this.selectedItems.size > 0) {
      if (!acceleratedCheckout.hasAttribute('data-accessory-previous-hidden')) {
        acceleratedCheckout.setAttribute(
          'data-accessory-previous-hidden',
          acceleratedCheckout.hasAttribute('hidden') ? 'true' : 'false'
        );
      }
      acceleratedCheckout.setAttribute('hidden', 'true');
      return;
    }

    const previousHidden = acceleratedCheckout.getAttribute('data-accessory-previous-hidden');
    if (previousHidden === 'false') {
      acceleratedCheckout.removeAttribute('hidden');
    }
    acceleratedCheckout.removeAttribute('data-accessory-previous-hidden');
  }

  handleVariantUpdate = (event) => {
    const productId = event.detail?.data?.productId;
    if (productId && this.productForm?.dataset.productId && productId !== this.productForm.dataset.productId) return;

    if (this.selectedItems.size > 0) {
      requestAnimationFrame(() => this.updateAcceleratedCheckout());
    }
  };

  handleParentCartUpdate = async (event) => {
    if (this.processing || this.selectedItems.size === 0) return;
    if (event.detail?.data?.source !== 'product-form-component') return;
    if (event.detail?.data?.didError) return;

    const eventProductId = event.detail?.data?.productId;
    const formProductId = this.productForm?.dataset.productId;
    if (eventProductId && formProductId && eventProductId !== formProductId) return;

    this.processing = true;
    this.setControlsDisabled(true);
    this.hideError();

    try {
      await this.addSelectedItemsToCart();
      this.selectedItems.clear();
      this.renderSelectedItems();
    } catch (error) {
      this.showError(error instanceof Error ? error.message : 'Some compatible items could not be added.');
    } finally {
      this.processing = false;
      this.setControlsDisabled(false);
      this.querySelectorAll('[data-accessory-row]').forEach((row) => this.syncRow(row));
    }
  };

  async addSelectedItemsToCart() {
    const selectedItems = Array.from(this.selectedItems.values());
    const items = selectedItems.map((item) => ({
      id: Number(item.variantId),
      quantity: item.quantity,
    }));

    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component').forEach((cartItems) => {
      if (cartItems instanceof HTMLElement && cartItems.dataset.sectionId) {
        sectionIds.add(cartItems.dataset.sectionId);
      }
    });

    const response = await fetch(
      Theme.routes.cart_add_url,
      fetchConfig('json', {
        body: JSON.stringify({
          items,
          sections: Array.from(sectionIds).join(','),
          sections_url: window.location.pathname,
        }),
      })
    );
    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Some compatible items could not be added.');
    }

    const cartResponse = await fetch(window.Shopify?.routes?.root + 'cart.js');
    const cart = cartResponse.ok ? await cartResponse.json() : {};
    const itemCount = selectedItems.reduce((sum, item) => sum + item.quantity, 0);

    document.dispatchEvent(
      new CartUpdateEvent(cart, 'product-accessory-selector', {
        source: 'product-accessory-selector',
        itemCount,
        sections: data.sections || {},
      })
    );
  }

  /** @param {boolean} disabled */
  setControlsDisabled(disabled) {
    this.querySelectorAll('button, select, input').forEach((control) => {
      if (
        control instanceof HTMLButtonElement ||
        control instanceof HTMLSelectElement ||
        control instanceof HTMLInputElement
      ) {
        control.disabled = disabled;
      }
    });
  }

  /** @param {string} message */
  showError(message) {
    if (!this.errorElement) return;

    this.errorElement.textContent = message;
    this.errorElement.hidden = false;
  }

  hideError() {
    if (!this.errorElement) return;

    this.errorElement.textContent = '';
    this.errorElement.hidden = true;
  }
}

if (!customElements.get('product-accessory-selector')) {
  customElements.define('product-accessory-selector', ProductAccessorySelector);
}
